import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	LeaderMetricItem,
	LeaderMetricsData,
	LeaderRewardService,
	LeaderUserService,
	User,
} from '@services/leader';
import {
	Group,
	GroupService,
	Organization,
	OrganizationService,
} from '@services/member';
import { LeaderboardCard } from '@services/member/rewards.service';
import {
	AsyncDelayService,
	LanguageService,
	MainKey,
	StorageService,
} from '@services/public';
import { AVATARS, getAvatarCoordinates, Select } from '@util';
import { JDate } from 'jakapa-utilities-lite';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

const PAGE_PATH = `pages.leader.dashboard.leaderReward`;

@Component({
	selector: 'app-leader-reward',
	templateUrl: './leader-reward.component.html',
	styleUrls: ['./leader-reward.component.scss'],
})
export class LeaderRewardComponent implements OnInit, OnDestroy {
	// Page filters.
	filters = {
		organization: new Select<Organization>(),
		group: new Select<Group>(),
	};

	// leaderboard
	leaderboard: Array<LeaderboardCard> | null = [];
	top3Leaderboard: Array<LeaderboardCard> | null = [];
	lower5Leaderboard: Array<LeaderboardCard> | null = [];
	userUUID: any = '';

	// metrics
	startDate: string = this.findMonday();
	currentPage: number = 1;
	metricsData: LeaderMetricsData;
	metricsItems: Array<LeaderMetricItem> = [];
	pagination = this.setupPagination();
	private _subscriptions: Subscription = new Subscription();
	form: UntypedFormGroup;
	startDateInput: HTMLElement;
	sortColumn: string = 'score';
	sortOrder: string = 'DESC';
	users: Array<User> | null = null;

	// Synchronous loading.
	private _loadMetricsKey: symbol = Symbol();

	// Page language.
	page: { [key: string]: string } = {};

	constructor(
		private _asyncDelaySvc: AsyncDelayService,
		private _groupSvc: GroupService,
		private _languageSvc: LanguageService,
		private _organizationSvc: OrganizationService,
		private _rewardSvc: LeaderRewardService,
		private _storageSvc: StorageService,
		private _userSvc: LeaderUserService,
	) {}

	ngOnInit(): void {
		this._languageSvc.get([PAGE_PATH]).then((value) => {
			if (typeof value[PAGE_PATH] === 'object' && value[PAGE_PATH] !== null)
				this.page = value[PAGE_PATH];
		});

		this.form = new UntypedFormGroup(
			{
				startDate: new UntypedFormControl(this.stringToDate(this.startDate), [
					Validators.required,
				]),
			},
			this.dateValidator,
		);

		// Set filter events.
		this._subscriptions.add(
			this.filters.organization.onChange.subscribe(
				(organization: Organization) => {
					this._storageSvc.setMainStorage(
						MainKey.SELECTED_ORGANIZATION_UUID,
						organization.uuid,
					);
					this.getLeaderboardData(this.filters.organization.value.uuid);
					this._asyncDelaySvc.asyncDelay(
						this._loadMetricsKey,
						this.loadMetrics.bind(this),
					);
				},
			),
		);
		this._subscriptions.add(
			this.filters.group.onChange.subscribe(() => {
				this._asyncDelaySvc.asyncDelay(
					this._loadMetricsKey,
					this.loadMetrics.bind(this),
				);
			}),
		);

		// Initialize filters.
		this._groupSvc
			.getGroups(false, (group: Group) => group.role !== null)
			.then(async (value: Array<Group>) => {
				this._subscriptions.add(
					this.filters.organization.onChange.subscribe(
						(organization: Organization) => {
							if (!organization || organization.uuid === null) {
								this.filters.group.options = value;
							} else
								this.filters.group.options = value.filter(
									(group: Group) =>
										group.uuid === null ||
										group.organizationUUID === organization.uuid,
								);
						},
					),
				);
				this.filters.group.loaded = true;
			});
		this._organizationSvc
			.getOrganizations((organization: Organization) => organization.leader)
			.then(async (value: Array<Organization>) => {
				await this.filters.group.loaded;
				this.filters.organization.options = value;
				this.filters.organization.value =
					this.filters.organization.options.find(
						(organization: Organization) =>
							organization.uuid ===
							this._storageSvc.getMainStorage(
								MainKey.SELECTED_ORGANIZATION_UUID,
							),
					) || this.filters.organization.value;
				this.filters.organization.loaded = true;
			});

		this._userSvc.getUsers().subscribe(async (res) => {
			this.users = res;
			await this.filters.organization.loaded;
			await this.filters.group.loaded;
			this.loadMetrics();
		});
	}

	get f(): any {
		return this.form.controls;
	}

	private dateValidator(form: UntypedFormGroup): any {
		if (!form.value.startDate) return { dateConflict: true };
	}

	private stringToDate(str: string): Date {
		return new Date(+str.slice(0, 4), +str.slice(5, 7) - 1, +str.slice(8, 10));
	}

	openDatepicker(startDateField) {
		startDateField.click();
	}

	getAvatarStyle(avatar: AVATARS, size?: number) {
		const { x, y } = getAvatarCoordinates(avatar, size);
		return { 'background-position': `${-x}px ${-y}px` };
	}

	loadMetrics() {
		// Skip if users aren't loaded yet.
		if (!this.users) return;

		this._rewardSvc
			.loadMetricsData(
				this.startDate,
				this.filters.group.value.uuid,
				this.currentPage,
				this.sortOrder,
				this.sortColumn,
			)
			.pipe(
				map((response: any) => {
					if (!response.errors) {
						this.metricsItems = [];
						this.metricsData = response;
						this.metricsData.records.forEach((metricItem) =>
							this.getUser(metricItem),
						);
						this.pagination = this.setupPagination();
					}
				}).bind(this),
			)
			.subscribe();
	}

	findMonday(): string {
		const jdate = new JDate();
		const diff = jdate.weekday === 0 ? 6 : jdate.weekday - 1;
		const newDate = jdate.addDays(-diff);
		return newDate.toString();
	}

	convertDateFormat(dateStr: string): string {
		const parts = dateStr.split('/');
		return `${parts[2]}-${parts[0]}-${parts[1]}`;
	}

	updateMetricsDate() {
		this.currentPage = 1;
		this.startDate = new JDate(this.form.value.startDate).toString();
		this.loadMetrics();
	}

	setupPagination() {
		let tempPagination = [];
		if (this.metricsData == undefined || this.metricsData == null) return [];
		if (this.metricsData.metadata.totalPages <= 4) {
			for (let x = 1; x <= this.metricsData.metadata.totalPages; x++) {
				tempPagination.push({
					pageNum: x,
					disabled: this.currentPage == x ? true : false,
				});
			}
			return tempPagination;
		}
		if (this.metricsData.metadata.totalPages > 4) {
			if (this.currentPage <= 3) {
				for (let x = 1; x <= this.metricsData.metadata.totalPages; x++) {
					tempPagination.push({
						pageNum: x,
						disabled: this.currentPage == x ? true : false,
					});
				}
				return tempPagination;
			}
			if (this.currentPage > this.metricsData.metadata.totalPages - 3) {
				let totalPages = this.metricsData.metadata.totalPages;
				for (
					let x = totalPages - 4;
					x <= this.metricsData.metadata.totalPages;
					x++
				) {
					tempPagination.push({
						pageNum: x,
						disabled: this.currentPage == x ? true : false,
					});
				}
				return tempPagination;
			}
			if (
				this.currentPage >= 3 &&
				this.currentPage <= this.metricsData.metadata.totalPages - 3
			) {
				let selectedPage = this.currentPage;
				return [
					{
						pageNum: selectedPage - 2,
						disabled: false,
					},
					{
						pageNum: selectedPage - 1,
						disabled: false,
					},
					{
						pageNum: selectedPage,
						disabled: true,
					},
					{
						pageNum: selectedPage + 1,
						disabled: false,
					},
					{
						pageNum: selectedPage + 2,
						disabled: false,
					},
				];
			}
		}
	}

	goToUserLeaderboard(userUUID) {
		return; // TODO - Add access to member reward page
	}

	pageBtnClick(destination: string | number) {
		switch (destination) {
			case 'prev': {
				this.currentPage--;
				break;
			}
			case 'next': {
				this.currentPage++;
				break;
			}
			default: {
				this.currentPage = <number>destination;
				break;
			}
		}
		this.loadMetrics();
	}

	// users
	async getUser(metricItem?: any, leaderCard?: LeaderboardCard) {
		let userUUID = metricItem?.userUUID || leaderCard?.userUUID;
		const user = this.users.find((user) => user.uuid == userUUID);
		if (metricItem) {
			this.metricsItems.push({ ...metricItem, name: `${user.name}` });
		}
		if (leaderCard && user) {
			leaderCard.name = `${user.name}`;
		}
	}

	onSortChange(sortKey: string) {
		switch (sortKey) {
			case 'scoreHighToLow':
				this.sortColumn = 'score';
				this.sortOrder = 'DESC';
				break;
			case 'scoreLowToHigh':
				this.sortColumn = 'score';
				this.sortOrder = 'ASC';
				break;
			case 'extraCreditHighToLow':
				this.sortColumn = 'extraCredit';
				this.sortOrder = 'DESC';
				break;
			case 'extraCreditLowToHigh':
				this.sortColumn = 'extraCredit';
				this.sortOrder = 'ASC';
				break;
			case 'name':
				this.sortColumn = 'name';
				this.sortOrder = 'ASC';
				break;
			default:
				return;
		}

		this.loadMetrics();
	}

	// leaderboard
	getLeaderboardData(orgUUID?: string) {
		this._rewardSvc
			.loadLeaderboardData(orgUUID, this.userUUID)
			.pipe(
				map((response: any) => {
					if (!response.errors) {
						this.leaderboard = response;
						this.buildLeaderboard(response);
					}
				}).bind(this),
			)
			.subscribe();
	}

	async buildLeaderboard(
		leaderboardData: Array<LeaderboardCard>,
	): Promise<void> {
		if (this.filters.organization.options.length == 0) return;
		if (this.leaderboard.length == 0) return;
		await Promise.all(
			leaderboardData.map((leaderCard, idx) => this.getUser(null, leaderCard)),
		);
		this.top3Leaderboard = [];
		this.lower5Leaderboard = [];
		leaderboardData.forEach((leaderCard, idx) => {
			//sorting out the top 3
			let rankDelta = ` ${leaderCard.rankDelta}`;
			let score: string = parseInt(leaderCard.score).toString();
			if (idx <= 2) {
				let self = '';
				if (this.userUUID == leaderCard.userUUID) self = ' self';
				// create ADA friendly top results where the middle item is still read first
				// when using a screenreader
				if (idx == 0)
					return this.top3Leaderboard.push({
						...leaderCard,
						class: `middle-user${self} ${rankDelta}`,
						score: score,
						size: 80,
					});
				if (idx == 1)
					return this.top3Leaderboard.push({
						...leaderCard,
						class: `left-user${self} ${rankDelta}`,
						score: score,
					});
				return this.top3Leaderboard.push({
					...leaderCard,
					class: rankDelta,
					score: score,
				});
			} else {
				// check for uuid to match current user
				if (this.userUUID == leaderCard.userUUID) {
					if (idx == 7)
						return this.lower5Leaderboard.push({
							...leaderCard,
							class: `self${rankDelta}`,
							idx: idx,
							score: score,
						});
					return this.lower5Leaderboard.push({
						...leaderCard,
						class: `self${rankDelta}`,
						score: score,
					});
				} else {
					return this.lower5Leaderboard.push({
						...leaderCard,
						class: rankDelta,
						score: score,
					});
				}
			}
		});
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}
}
